/**
 * Componente principal da aplicação Mental Health Portal.
 *
 * Este componente é responsável por:
 * - Configurar o roteamento da aplicação
 * - Gerenciar a autenticação do usuário
 * - Renderizar o layout principal com navegação lateral e cabeçalho
 * - Configurar o token de autenticação para requisições à API
 *
 * @module App
 */

import { useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';

import Header from './components/Header/Header';
import SideNav from './components/SideNav/SideNav';
import logger from './logger';
import AIPrompts from './pages/AIPrompts/AIPrompts';
import CreateQuestionnairePage from './pages/CreateQuestionnaire/CreateQuestionnaire';
import Dashboard from './pages/Dashboard/Dashboard';
import EditQuestionnairePage from './pages/EditQuestionnairePage/EditQuestionnairePage';
import EditResources from './pages/EditResources/EditResources';
import Employees from './pages/Employees/Employees';
import Example from './pages/Example/Example';
import Messages from './pages/Messages/Messages';
import NewResources from './pages/NewResources/NewResources';
import Questionnaires from './pages/Questionnaires/Questionnaires';
import ReportsTabs from './pages/ReportsTabs/ReportsTabs';
import Resources from './pages/Resources/Resources';
import Settings from './pages/Settings/Settings';
import Questions from './pages/Questions/Questions';
import { setToken } from './services/api';
import LoadingPage from './components/LoadingPage';

/**
 * Componente funcional principal da aplicação.
 *
 * Gerencia o estado de autenticação e renderiza o layout principal
 * com todas as rotas disponíveis na aplicação.
 *
 * @returns {JSX.Element} Componente principal da aplicação
 */
function App() {
  // Hook de autenticação OIDC
  const auth = useAuth();
  const [tokenLoaded, setTokenLoaded] = useState(false);

  /**
   * Efeito que configura o token de autenticação para requisições à API
   * quando o estado de autenticação muda.
   */
  useEffect(() => {
    if (auth.isLoading) return;
    setToken(auth.user?.id_token);
    setTokenLoaded(!!auth.user?.id_token);
  }, [auth.isLoading, auth.user?.id_token]);

  // Exibe indicador de carregamento enquanto a autenticação está sendo verificada
  if (auth.isLoading) return <LoadingPage />;

  // Redireciona para a página de login se o usuário não estiver autenticado
  if (!auth.isAuthenticated) {
    logger.debug('not authenticated: ', auth);
    auth.signinRedirect();
  }

  // Wait for token to load on axios instance
  if (!tokenLoaded) return <LoadingPage />;

  return (
    <div className="main-container">
      <BrowserRouter>
        {/* Navegação lateral */}
        <SideNav />
        <div className="content-container">
          {/* Cabeçalho da aplicação */}
          <Header />

          {/* Configuração de rotas */}
          <Routes>
            {/* Rota padrão redireciona para o dashboard */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />

            {/* Rotas principais */}
            <Route path="/examples" element={<Example />} />
            <Route path="/aiprompts" element={<AIPrompts />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/employees" element={<Employees />} />
            <Route path="/messages" element={<Messages />} />
            <Route path="/questions" element={<Questions />} />

            <Route path="/questionnaires" element={<Questionnaires />} />
            <Route path="/reports" element={<ReportsTabs />} />

            {/* Rotas para questionários */}
            <Route
              path="/createQuestionnaire"
              element={<CreateQuestionnairePage />}
            />
            <Route
              path="/editQuestionnaire/:id"
              element={<EditQuestionnairePage />}
            />

            {/* Rotas para recursos */}
            <Route path="/resources" element={<Resources />} />
            <Route path="/newResources" element={<NewResources />} />
            <Route path="/editResources" element={<EditResources />} />

            {/* Configurações */}
            <Route path="/settings" element={<Settings />} />
          </Routes>
        </div>
      </BrowserRouter>
    </div>
  );
}

export default App;
